import {customElement, property, state} from "lit/decorators.js";
import {LitElement, html, CSSResult, unsafeCSS} from "lit";
import {ReservationFormConfig, ReservationFormData} from "../../@types/reservation-form.js";
import styles from './step-four.scss';
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {consume} from "@lit/context";
import {i18nContext} from "./i18nContext.js";
import {Messages} from "../../@types/online-reservation.js";
import './reservation-badges/reservation-badges.js';
import { BdkTextField } from '@breuni-ui/bdk-text-field';
import { BdkCheckbox } from '@breuni-ui/bdk-checkbox';
import { BdkRadioButton } from '@breuni-ui/bdk-radio-button';
import { safeDefine } from '@breuni-ui/safe-define';

safeDefine('bdk-ents-text-field', BdkTextField);
safeDefine('bdk-ents-checkbox', BdkCheckbox);
safeDefine('bdk-ents-radio-button', BdkRadioButton);

@customElement('step-four')
export class StepFour extends LitElement {
  @property({ type: Function }) onNext!: () => void;
  @property({ type: Function }) handleSuccessfulReservation!: () => void;
  @property({ type: Function }) handleFailedReservation!: (errorMessage: string) => void;

  @property({type: Object, reflect: false}) config: ReservationFormConfig = {
    storeLocation: {
      name: '',
      storeId: 0
    },
    articleId: '',
    size: '',
    dataProtectionRegulationLink: '',
    reservationEndpoint: '',
  };

  @consume({context: i18nContext})
  private readonly messages!: Messages;

  @state() private formData: ReservationFormData = {
    salutation: 'ms',
    firstName: '',
    lastName: '',
    email: '',
    newsletter: false
  };

  @state() private fieldErrors: Record<string, boolean> = {
    firstName: false,
    lastName: false,
    email: false
  };

  @property({ type: Boolean })
  private showSizeBadge = true;

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  private static readonly localPart =
    '(?:[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+' +
    '(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*' +
    '|"(?:[\\x20-\\x21\\x23-\\x5b\\x5d-\\x7e]' +
    '|\\\\[\\x20-\\x7e])*")';

  // Domain name (e.g. example.com)
  private static readonly domain =
    '(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+' +
    '[a-zA-Z]{2,}';

  // IPv4 address in brackets (e.g. [123.45.67.89])
  private static readonly ipv4 = '\\[(?:\\d{1,3}\\.){3}\\d{1,3}\\]';

  private static readonly EMAIL_REGEXP = new RegExp(`^${StepFour.localPart}@(?:${StepFour.domain}|${StepFour.ipv4})$`);

  private handleInputChange(event: Event) {
    const target = event.target as HTMLElement;
    let name: string | null | undefined;
    let value: string | boolean | null | undefined;

    // Handle BDK text field component (based on wishlist implementation)
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as BdkTextField;
      name = bdkField.getAttribute('name') ?? '';
      value = bdkField.value;
    }
    // Handle BDK checkbox component
    else if (target.tagName === 'BDK-ENTS-CHECKBOX') {
      const bdkCheckbox = target as BdkCheckbox;
      name = bdkCheckbox.name || '';
      value = bdkCheckbox.checked;
    }


    if (!name) {
      return;
    }

    this.formData = {
      ...this.formData,
      [name]: value
    };

    // Clear error when user starts typing
    if (this.fieldErrors[name]) {
      this.fieldErrors = {
        ...this.fieldErrors,
        [name]: false
      };
    }
  }

  private handleRadioChange(salutation: 'ms' | 'mr') {
    this.formData = {
      ...this.formData,
      salutation
    };
  }

  private validateField(event: Event) {
    const target = event.target as HTMLElement;
    let name: string;
    let value: string;

    // Handle BDK text field component
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as BdkTextField;
      name = bdkField.getAttribute('name') ?? '';
      value = bdkField.value;
    } else {
      name = '';
      value = '';
    }

    if (name in this.fieldErrors) {
      const isValid = value.trim() !== '';

      this.fieldErrors = {
        ...this.fieldErrors,
        [name]: !isValid
      };
    }
  }

  private validateEmail(event: Event) {
    const target = event.target as HTMLElement;
    let value: string;

    // Handle BDK text field component
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as BdkTextField;
      value = bdkField.value;
    } else {
      // Handle input events from shadow DOM
      const input = target as HTMLInputElement;
      const bdkTextFieldHost = (input.getRootNode() as ShadowRoot)?.host;
      if (bdkTextFieldHost?.tagName === 'BDK-ENTS-TEXT-FIELD') {
        const bdkField = bdkTextFieldHost as BdkTextField;
        value = bdkField.value;
      } else {
        value = input.value;
      }
    }

    this.fieldErrors = {
      ...this.fieldErrors,
      email: StepFour.validateEmailValue(value)
    };
  }

  private validateAllFields(): boolean {
    const newErrors = {
      firstName: this.formData.firstName.trim() === '',
      lastName: this.formData.lastName.trim() === '',
      email: StepFour.validateEmailValue(this.formData.email)
    };

    this.fieldErrors = newErrors;

    return !Object.values(newErrors).some(isError => isError);
  }

  private static validateEmailValue(emailValue: string): boolean {
    const email = emailValue.trim();
    return email === '' || !StepFour.EMAIL_REGEXP.test(email);
  }

  private submitReservation() {
    const isValid = this.validateAllFields();

    if (isValid) {
      const apiRequestData = {
        artikelListe: [
          {
            artikelNummer: this.config.articleId
          }
        ],
        haus: {
          nummer: this.config.storeLocation.storeId
        },
        kundenKontakt: {
          anrede: this.formData.salutation === 'ms' ? this.messages.salutationMs : this.messages.salutationMr,
          vorname: this.formData.firstName,
          nachname: this.formData.lastName,
          email: this.formData.email
        },
        newsletteranmeldung: {
          newsletteranmeldung: this.formData.newsletter,
          labelText: this.messages.newsletter
        }
      };

      // Make the API request
      fetch(this.config.reservationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiRequestData)
      })
      .then((response) => {
        if (response.ok) {
          // Status code 200 - Reservation successful
          this.handleSuccessfulReservation();
        } else {
          // Other error
          this.handleFailedReservation('online-reservation request failed!');
        }
      })
      .catch((error) => {
        console.error('Reservation request failed:', error);
        this.handleFailedReservation('');
      });
    }
  }

  render() {
    return html`
      <div class="ents-reservation-form">
        <h2 class="ents-reservation-form__title ents-heading-medium" data-test-ents-or-stepfour-header>
          ${this.messages.reservationFormTitle}
        </h2>
        <reservation-badges
          .sizeDescription=${this.config.size}
          .storeLocationName=${this.config.storeLocation.name}
          .showSizeBadge=${this.showSizeBadge}
        ></reservation-badges>
        <div class="ents-reservation-form__salutation">
          <bdk-ents-radio-button name="salutation" value="ms" size="medium" id="salutation-ms"
                            ?checked=${this.formData.salutation === 'ms'}
                            @change=${() => this.handleRadioChange('ms')}>
            ${this.messages.salutationMs}
          </bdk-ents-radio-button>
          <bdk-ents-radio-button name="salutation" value="mr" size="medium" id="salutation-mr"
                            ?checked=${this.formData.salutation === 'mr'}
                            @change=${() => this.handleRadioChange('mr')}>
            ${this.messages.salutationMr}
          </bdk-ents-radio-button>
        </div>

        <bdk-ents-text-field
          class="ents-reservation-form__field fs-mask"
          name="firstName"
          type="text"
          id="firstName"
          .value=${this.formData.firstName}
          @input=${this.handleInputChange}
          @blur=${this.validateField}
          required
          maxlength="100"
          aria-required="true"
          aria-describedby=${this.fieldErrors.firstName ? 'firstNameError' : undefined}
          ?invalid=${this.fieldErrors.firstName}
        >
          ${this.messages.firstName}

          <div slot="error" id="firstNameError" role="alert" aria-live="assertive">
            ${this.fieldErrors.firstName ? this.messages.firstNameError : ''}
          </div>
        </bdk-ents-text-field>

        <bdk-ents-text-field
          class="ents-reservation-form__field fs-mask"
          name="lastName"
          type="text"
          id="lastName"
          .value=${this.formData.lastName}
          @input=${this.handleInputChange}
          @blur=${this.validateField}
          required
          maxlength="100"
          aria-required="true"
          aria-describedby=${this.fieldErrors.lastName ? 'lastNameError' : undefined}
          ?invalid=${this.fieldErrors.lastName}
        >
          ${this.messages.lastName}

          <div slot="error" id="lastNameError" role="alert" aria-live="assertive">
            ${this.fieldErrors.lastName ? this.messages.lastNameError : ''}
          </div>
        </bdk-ents-text-field>

        <bdk-ents-text-field
          class="ents-reservation-form__field fs-mask"
          name="email"
          type="email"
          id="email"
          .value=${this.formData.email}
          @input=${this.handleInputChange}
          @blur=${this.validateEmail}
          required
          maxlength="100"
          aria-required="true"
          aria-describedby=${this.fieldErrors.email ? 'emailError' : undefined}
          ?invalid=${this.fieldErrors.email}
        >
          ${this.messages.email}

          <div slot="error" id="emailError" role="alert" aria-live="assertive">
            ${this.fieldErrors.email ? this.messages.emailError : ''}
          </div>
        </bdk-ents-text-field>

        <bdk-ents-checkbox
          class="ents-reservation-form__newsletter"
          id="newsletter"
          name="newsletter"
          ?checked=${this.formData.newsletter}
          @change=${this.handleInputChange}
          size="small"
        >
          ${ unsafeHTML(this.messages.newsletter)}
        </bdk-ents-checkbox>

        <div class="ents-reservation-form__privacy ents-copy-extra-small">
          ${this.messages.privacyInfo} <a href=${this.config.dataProtectionRegulationLink} target="_blank"
                                          class="bdk-link-inline">${this.messages.privacyLink}</a>.
        </div>


        <button class="bdk-button ents-reservation-form__btn"
                type="button"
                variant="primary"
                size="medium"
                full-width=""
                @click=${this.submitReservation}>
          ${this.messages.submitButton}
        </button>
      </div>
    `;
  }
}
